/** WhatsApp copy after approving a school fee payment. */

export function buildFeeApprovalMessage({
  schoolName,
  studentName,
  isNewStudent,
  askFeeling = true,
}) {
  const name = (studentName || "").trim() || "there";
  const school = (schoolName || "").trim() || "our school";

  if (isNewStudent) {
    return `Hi ${name}! Welcome to ${school} — you're all set for class. See you on the floor!`;
  }

  let text = `Hi ${name}! Thanks — your pass/credits are renewed at ${school}. Looking forward to dancing with you.`;
  if (askFeeling) {
    text += ` How are you feeling about class lately? Reply anytime.`;
  }
  return text;
}

export function phoneToWaMe(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  return digits ? `https://wa.me/${digits}` : null;
}

export function openFeeWhatsApp({ phone, message }) {
  const base = phoneToWaMe(phone);
  if (!base) return false;
  window.open(
    `${base}?text=${encodeURIComponent(message)}`,
    "_blank",
    "noopener,noreferrer",
  );
  return true;
}

/** Prefill when a host messages a guest from the dashboard guest list. */
export function buildHostWhatsAppGreeting({ hostName, schoolName } = {}) {
  const name = String(hostName || "").trim() || "your host";
  const school = String(schoolName || "").trim();
  if (school) return `Hello from ${name} (${school})!`;
  return `Hello from ${name}!`;
}

function hostSignature({ hostName, schoolName } = {}) {
  const host = String(hostName || "").trim();
  const school = String(schoolName || "").trim();
  if (host && school) return `${host} (${school})`;
  return host || school;
}

export function buildInviteDecisionWhatsAppMessage({
  guestName,
  eventName,
  accepted,
  hostName,
  schoolName,
} = {}) {
  const guest = String(guestName || "").trim() || "there";
  const event = String(eventName || "").trim() || "the event";
  const signature = hostSignature({ hostName, schoolName });
  const message = accepted
    ? `Hi ${guest}! Your invite to ${event} has been accepted — you're on the guest list. See you there!`
    : `Hi ${guest}, thanks for your interest in ${event}. We couldn't approve your invite this time.`;
  return signature ? `${message}\n\n— ${signature}` : message;
}

export function buildClassAdmissionWhatsAppMessage({
  studentName,
  className,
  sessionLabel,
  schoolName,
} = {}) {
  const student = String(studentName || "").trim() || "there";
  const classTitle = String(className || "").trim() || "class";
  const session = String(sessionLabel || "").trim();
  const school = String(schoolName || "").trim();
  let message = `Hi ${student}! You're confirmed for ${classTitle}`;
  if (session) message += ` — ${session}`;
  message += ". See you in class!";
  return school ? `${message}\n\n— ${school}` : message;
}

export function buildFeeRejectionMessage({ studentName, schoolName } = {}) {
  const name = String(studentName || "").trim() || "there";
  const school = String(schoolName || "").trim() || "our school";
  return `Hi ${name}, we couldn't approve your recent payment at ${school}. Reply here if you have questions — happy to help.`;
}

export function buildFeeChaseMessage({ studentName, schoolName } = {}) {
  const name = String(studentName || "").trim() || "there";
  const school = String(schoolName || "").trim() || "our school";
  return `Hi ${name}! Just checking in about your payment for ${school}. Let us know if you need help completing it.`;
}

export function buildRemoveRsvpWhatsAppMessage({
  guestName,
  eventName,
  hostName,
  schoolName,
} = {}) {
  const guest = String(guestName || "").trim() || "there";
  const event = String(eventName || "").trim() || "the event";
  const signature = hostSignature({ hostName, schoolName });
  const message = `Hi ${guest}, you've been removed from the guest list for ${event}. Message us if you have questions.`;
  return signature ? `${message}\n\n— ${signature}` : message;
}

export function buildEventCoAdminWhatsAppMessage({
  guestName,
  eventName,
  hostName,
  schoolName,
} = {}) {
  const guest = String(guestName || "").trim() || "there";
  const event = String(eventName || "").trim() || "the event";
  const signature = hostSignature({ hostName, schoolName });
  const message = `Hi ${guest}! You've been made a co-admin for ${event} — you can help manage the guest list and night-of details.`;
  return signature ? `${message}\n\n— ${signature}` : message;
}

export function buildAttendanceWhatsAppMessage({
  studentName,
  className,
  schoolName,
} = {}) {
  const student = String(studentName || "").trim() || "there";
  const classTitle = String(className || "").trim() || "class";
  const school = String(schoolName || "").trim();
  const message = `Hi ${student}! You're checked in for ${classTitle}. Enjoy class!`;
  return school ? `${message}\n\n— ${school}` : message;
}

export function buildNoShowWhatsAppMessage({
  studentName,
  className,
  schoolName,
  deducted = false,
} = {}) {
  const student = String(studentName || "").trim() || "there";
  const classTitle = String(className || "").trim() || "class";
  const school = String(schoolName || "").trim();
  const message = deducted
    ? `Hi ${student}, we marked you as a no-show for ${classTitle} and deducted 1 credit. Reply if that doesn't look right.`
    : `Hi ${student}, we marked you absent for ${classTitle}. Hope to see you next time!`;
  return school ? `${message}\n\n— ${school}` : message;
}

export function buildCreditAdjustWhatsAppMessage({
  studentName,
  schoolName,
  delta,
} = {}) {
  const student = String(studentName || "").trim() || "there";
  const school = String(schoolName || "").trim() || "our school";
  const amount = Math.abs(Number(delta) || 0);
  const unit = amount === 1 ? "credit" : "credits";
  if (Number(delta) > 0) {
    return `Hi ${student}! We've added ${amount} ${unit} to your pack at ${school}.`;
  }
  if (Number(delta) < 0) {
    return `Hi ${student}, we've removed ${amount} ${unit} from your pack at ${school}. Reply if you have questions.`;
  }
  return `Hi ${student} — a quick note about your credits at ${school}.`;
}

export function buildSchoolRoleWhatsAppMessage({
  studentName,
  schoolName,
  role,
} = {}) {
  const student = String(studentName || "").trim() || "there";
  const school = String(schoolName || "").trim() || "our school";
  if (role === "ADMIN") {
    return `Hi ${student}! You've been made a co-admin at ${school} — you can help manage classes, fees, and people.`;
  }
  if (role === "INSTRUCTOR") {
    return `Hi ${student}! You've been added as an instructor at ${school}.`;
  }
  if (role === "REMOVED") {
    return `Hi ${student}, your team access at ${school} has been removed. You can still follow as a student.`;
  }
  return `Hi ${student}! Your role at ${school} has been updated.`;
}

export function buildLateCancelResolutionWhatsAppMessage({
  studentName,
  className,
  schoolName,
  forgiven,
} = {}) {
  const student = String(studentName || "").trim() || "there";
  const classTitle = String(className || "").trim() || "class";
  const school = String(schoolName || "").trim() || "our school";
  return forgiven
    ? `Hi ${student}! About your late cancel for ${classTitle} at ${school} — we've waived the penalty / reset your offer.`
    : `Hi ${student}, about your late cancel for ${classTitle} at ${school} — we've marked the offer as used. Reply if you have questions.`;
}

export function buildPersonSheetWhatsAppGreeting({
  studentName,
  schoolName,
} = {}) {
  const student = String(studentName || "").trim() || "there";
  const school = String(schoolName || "").trim();
  return school ? `Hi ${student} — reaching out from ${school}.` : `Hi ${student} —`;
}

/** Prefer Action Items when there are pending fees/items and no explicit ?tab=. */
export function resolveSchoolAdminTab({ tabParam, pendingCount = 0, tabs, fallback = "classes" }) {
  const normalized = tabParam === "fees" ? "action-items" : tabParam;
  if (normalized && tabs.includes(normalized)) return normalized;
  if (pendingCount > 0) return "action-items";
  return fallback;
}

/** Nested under the Studio parent tab in the school admin UI. URLs stay `?tab=<id>`. */
export const SCHOOL_STUDIO_TABS = ["profile", "venues", "classes", "products"];

export const SCHOOL_ADMIN_TABS = ["action-items", "people", ...SCHOOL_STUDIO_TABS];

/** Legacy `?tab=fees` still resolves to Action Items. */
export function normalizeSchoolAdminTab(tabParam) {
  if (tabParam === "fees") return "action-items";
  return tabParam;
}

export function isSchoolStudioTab(tab) {
  return SCHOOL_STUDIO_TABS.includes(tab);
}

export const CANCELLATION_DEADLINE_OPTIONS = [
  { value: "ANYTIME", label: "Anytime before class" },
  { value: "START", label: "Until class start" },
  { value: "1H", label: "1 hour before" },
  { value: "6H", label: "6 hours before" },
  { value: "12H", label: "12 hours before" },
  { value: "24H", label: "24 hours before" },
  { value: "48H", label: "48 hours before" },
];

export const OFFER_TYPE_OPTIONS = [
  { value: "FIRST_CLASS_FREE", label: "First Class Free" },
];
